import { useState, useEffect, useRef } from 'react';
import { Bot, ExternalLink, RotateCcw, Loader2 } from 'lucide-react';
import { supabaseClient } from '../../../../lib/supabase';

const WEBHOOK_URL = 'https://secondbrain.insigte.com';
const WEBHOOK_SECRET = import.meta.env.VITE_OPTIMIZE_WEBHOOK_SECRET ?? '';

type OptState = 'idle' | 'processing' | 'done' | 'error';

interface AiResult {
  status?: string;
  doc_url?: string;
  error?: string;
  started_at?: string;
}

interface Props {
  analysisId: string;
}

function parseAiResult(raw: unknown): AiResult | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw as AiResult;
}

function useElapsed(startedAt: string | null): string {
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!startedAt) return;
    const update = () => {
      const secs = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      setElapsed(`${m}:${String(s).padStart(2, '0')}`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return elapsed;
}

export function OptimizeButton({ analysisId }: Props) {
  const [state, setState] = useState<OptState>('idle');
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsed = useElapsed(state === 'processing' ? startedAt : null);

  // Load initial state from Supabase on mount
  useEffect(() => {
    supabaseClient
      .from('analyses')
      .select('ai_result, docs_url')
      .eq('id', analysisId)
      .single()
      .then(({ data }) => {
        if (!data) return;
        const r = parseAiResult(data.ai_result);
        if (r?.status === 'done') {
          setState('done');
          setDocUrl(r.doc_url ?? data.docs_url ?? null);
        } else if (r?.status === 'processing') {
          setState('processing');
          setStartedAt(r.started_at ?? new Date().toISOString());
        } else if (r?.status === 'error') {
          setState('error');
          setErrMsg(r.error?.slice(0, 120) ?? 'Unknown error');
        }
      });
  }, [analysisId]);

  // Poll while processing
  useEffect(() => {
    if (state !== 'processing') {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(async () => {
      const { data } = await supabaseClient
        .from('analyses')
        .select('ai_result, docs_url')
        .eq('id', analysisId)
        .single();
      if (!data) return;
      const r = parseAiResult(data.ai_result);
      if (r?.status === 'done') {
        setState('done');
        setDocUrl(r.doc_url ?? data.docs_url ?? null);
        window.location.reload();
      } else if (r?.status === 'error') {
        setState('error');
        setErrMsg(r.error?.slice(0, 120) ?? 'Unknown error');
      }
    }, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [state, analysisId]);

  const handleOptimize = async () => {
    setState('processing');
    setStartedAt(new Date().toISOString());
    setErrMsg(null);
    const res = await fetch(`${WEBHOOK_URL}/api/ycp/optimize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Secret': WEBHOOK_SECRET,
      },
      body: JSON.stringify({ analysis_id: analysisId }),
    });
    if (!res.ok) {
      setState('error');
      setErrMsg('Failed to start');
    }
  };

  if (state === 'processing') {
    return (
      <button
        disabled
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-400 cursor-not-allowed"
      >
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Optimizing…{elapsed ? ` ${elapsed}` : ''}
      </button>
    );
  }

  if (state === 'done') {
    return (
      <div className="inline-flex items-center gap-1">
        {docUrl && (
          <a
            href={docUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Doc
          </a>
        )}
        <button
          onClick={handleOptimize}
          title="Re-run optimization"
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <button
        onClick={handleOptimize}
        title={errMsg ?? 'Error'}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
      >
        <RotateCcw className="h-4 w-4 mr-2" />
        Retry
      </button>
    );
  }

  return (
    <button
      onClick={handleOptimize}
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
    >
      <Bot className="h-4 w-4 mr-2" />
      Optimize
    </button>
  );
}
